import { ChangeDetectionStrategy, Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

/**
 * Campo de Sim/Não do sistema: a chave deslizante com o estado escrito ao lado.
 *
 * Existe como componente, e não como marcação repetida, porque a mesma pergunta binária aparece em
 * vários formulários (divulgar, isento, apresentou diploma…) e cada cópia da marcação era uma chance
 * a mais de o rótulo, o estado desabilitado ou o texto do estado saírem diferentes — foi o que
 * aconteceu: parte das telas usava chave deslizante e parte usava botão de Sim/Não.
 *
 * Implementa `ControlValueAccessor`, então serve tanto a `formControlName` quanto a `[(ngModel)]`.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-switch-sim-nao',
    standalone: true,
    imports: [ToggleSwitchModule, FormsModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            // `forwardRef` porque o provider é declarado antes de a classe existir.
            useExisting: forwardRef(() => SwitchSimNao),
            multi: true
        }
    ],
    styleUrl: './switch-sim-nao.scss',
    templateUrl: './switch-sim-nao.html'
})
export class SwitchSimNao implements ControlValueAccessor {
    /** `id` do input interno, para o `for` de um `<label>` de fora apontar para cá. */
    readonly inputId = input<string>('');
    /** Usado quando não há `<label>` visível ligado ao campo. */
    readonly ariaLabel = input<string>('');
    /** Desabilita por decisão da tela (modo leitura), independente do estado do controle. */
    readonly desabilitado = input(false);

    protected readonly valor = signal(false);
    /** Vem de `setDisabledState`, quando é o próprio controle do formulário que está desabilitado. */
    private readonly desabilitadoPeloFormulario = signal(false);
    protected readonly bloqueado = computed(() => this.desabilitado() || this.desabilitadoPeloFormulario());

    private aoTocar: () => void = () => {};
    private aoMudarValor: (valor: boolean) => void = () => {};

    writeValue(valor: boolean | null): void {
        this.valor.set(!!valor);
    }

    registerOnChange(fn: (valor: boolean) => void): void {
        this.aoMudarValor = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.aoTocar = fn;
    }

    setDisabledState(desabilitado: boolean): void {
        this.desabilitadoPeloFormulario.set(desabilitado);
    }

    protected alternar(valor: boolean): void {
        // Guarda de fecho: o `readonly` do PrimeNG já barra o clique e o teclado, mas ele é a camada
        // de interação. Quem responde pelo VALOR é este método, e em leitura ele não escreve.
        if (this.bloqueado()) {
            return;
        }
        this.valor.set(valor);
        this.aoMudarValor(valor);
        this.aoTocar();
    }
}
